import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  FormHelperText,
  Divider,
  Chip,
  Avatar,
  Autocomplete,
  Alert,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Paper,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { X, GitBranch, ArrowRight } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';

import { useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { TaskPriority, TaskStatus, Task, TaskRACI } from '@/types';
import { taskService, userService, configService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import type { User } from '@/types';
import PriorityBadge from '@/shared/components/PriorityBadge';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';
import { formatPercent } from '@/shared/utils/formatPercentage';

// ============================================================================
// SCHEMA
// ============================================================================

// Create dynamic schema based on config
const createSubtaskSchema = (maxEstimatedMinutes: number) => z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title too long'),
  description: z.string().max(2000, 'Description too long').optional(),
  priority: z.nativeEnum(TaskPriority),
  startDate: z.any().nullable().optional(),
  dueDate: z.any().nullable().optional(),
  estimatedMinutes: z.number().min(0, 'Must be positive').max(maxEstimatedMinutes, `Max ${Math.floor(maxEstimatedMinutes / 60)} hours`).optional(),
  isOnsite: z.boolean().optional(),
  onsiteLocation: z.string().max(200, 'Location too long').optional(),
  raci: z.object({
    responsible: z.array(z.string()).min(1, 'Select at least one responsible'),
    accountable: z.string().min(1, 'Select one accountable person'),
    consulted: z.array(z.string()).min(1, 'Select at least one consulted'),
    informed: z.array(z.string()).min(1, 'Select at least one informed'),
  }),
});

// Default schema for initial setup
const subtaskSchema = createSubtaskSchema(480);

type SubtaskFormData = z.infer<typeof subtaskSchema>;

// ============================================================================
// TYPES
// ============================================================================

interface CreateSubtaskModalProps {
  open: boolean;
  onClose: () => void;
  parentTask: Task;
  onSuccess?: (newTask: Task) => void;
}

function userDisplayName(u: User): string {
  return u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
}

// ============================================================================
// COMPONENT
// ============================================================================

const CreateSubtaskModal: React.FC<CreateSubtaskModalProps> = ({
  open,
  onClose,
  parentTask,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const currentUser = useAppSelector(selectCurrentUser);
  const { showError, showSuccess } = useSnackbar();

  // Fetch max estimated hours config
  const { data: maxEstimatedConfig } = useQuery({
    queryKey: ['config', 'task.max_estimated_hours'],
    queryFn: () => configService.getConfigByKey('task.max_estimated_hours'),
    enabled: open,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const maxEstimatedMinutes = React.useMemo(() => {
    if (!maxEstimatedConfig?.value) return 480;
    const value = typeof maxEstimatedConfig.value === 'number' 
      ? maxEstimatedConfig.value 
      : parseInt(String(maxEstimatedConfig.value), 10);
    return isNaN(value) ? 480 : value;
  }, [maxEstimatedConfig]);

  // Fetch RACI-assignable users (includes admins) filtered by project
  const { data: usersData, isLoading: usersLoading, error: usersError } = useQuery({
    queryKey: ['raci-assignable-users', parentTask.projectId],
    queryFn: () => {
      console.log('[CreateSubtask] Fetching RACI users for project:', parentTask.projectId);
      return userService.getRACIAssignableUsers(parentTask.projectId || undefined);
    },
    enabled: open,
    staleTime: 0, // Always fetch fresh data when dialog opens
    refetchOnMount: 'always', // Refetch every time the modal opens
  });
  const users = usersData ?? [];

  // Fetch all tasks to get existing subtasks of the parent
  const { data: allTasksData } = useQuery({
    queryKey: QUERY_KEYS.tasks,
    queryFn: () => taskService.getTasks(),
    enabled: open,
  });

  // Recursive function to get total time including all nested subtasks
  const getTotalTimeWithNested = React.useCallback((taskId: string, allTasks: Task[]): number => {
    // Find direct children of this task
    const directChildren = allTasks.filter((t: Task) => t.parentTaskId === taskId);
    
    // Calculate time for direct children
    let totalTime = directChildren.reduce(
      (sum: number, child: Task) => sum + (child.estimatedMinutes || 0),
      0
    );
    
    // Recursively add time for nested children
    directChildren.forEach((child: Task) => {
      totalTime += getTotalTimeWithNested(child.id, allTasks);
    });
    
    return totalTime;
  }, []);

  // Calculate time budget
  const timeBudget = React.useMemo(() => {
    const parentEstimatedMinutes = parentTask.estimatedMinutes || 0;
    const allTasks = (allTasksData?.items || []) as Task[];
    
    // Get direct subtasks
    const directSubtasks = allTasks.filter(
      (t: Task) => t.parentTaskId === parentTask.id
    );
    
    // Calculate total time including ALL nested subtasks recursively
    const totalExistingSubtaskMinutes = getTotalTimeWithNested(parentTask.id, allTasks);
    
    const remainingMinutes = parentEstimatedMinutes - totalExistingSubtaskMinutes;
    const usagePercent = parentEstimatedMinutes > 0 
      ? (totalExistingSubtaskMinutes / parentEstimatedMinutes) * 100 
      : 0;

    console.log('[CreateSubtask] Time Budget Calculation:', {
      parentId: parentTask.id,
      parentEstimatedMinutes,
      directSubtasksCount: directSubtasks.length,
      totalExistingSubtaskMinutes,
      remainingMinutes,
      usagePercent: usagePercent.toFixed(2),
    });

    return {
      parentTotal: parentEstimatedMinutes,
      existingSubtasksTotal: totalExistingSubtaskMinutes,
      remaining: remainingMinutes,
      usagePercent,
      subtasksCount: directSubtasks.length,
    };
  }, [parentTask, allTasksData, getTotalTimeWithNested]);

  // Calculate effective max estimated time (minimum of config max and remaining time)
  const effectiveMaxMinutes = React.useMemo(() => {
    const remaining = Math.max(0, timeBudget.remaining);
    return Math.min(maxEstimatedMinutes, remaining);
  }, [maxEstimatedMinutes, timeBudget.remaining]);

  // Create resolver dynamically based on effective max
  const subtaskResolver = React.useMemo(() => {
    return zodResolver(createSubtaskSchema(effectiveMaxMinutes));
  }, [effectiveMaxMinutes]);

  // Debug logging
  React.useEffect(() => {
    if (open) {
      console.log('[CreateSubtask] Modal opened', { 
        projectId: parentTask.projectId, 
        usersCount: users.length,
        usersLoading,
        usersError,
        timeBudget: timeBudget,
        effectiveMaxMinutes
      });
    }
  }, [open, users.length, parentTask.projectId, usersLoading, usersError, timeBudget, effectiveMaxMinutes]);

  // Form setup with defaults inherited from parent
  const {
    control,
    handleSubmit,
    reset,
    watch,
    trigger,
    formState: { errors, isSubmitting, isValid },
  } = useForm<SubtaskFormData>({
    resolver: subtaskResolver,
    mode: 'onTouched',
    defaultValues: {
      title: '',
      description: '',
      priority: parentTask.priority,
      startDate: parentTask.startDate ? dayjs(parentTask.startDate) : dayjs(),
      dueDate: parentTask.dueDate ? dayjs(parentTask.dueDate) : dayjs().add(1, 'day'),
      estimatedMinutes: 60,
      isOnsite: parentTask.isOnsite || false,
      onsiteLocation: parentTask.onsiteLocation || '',
      raci: {
        responsible: parentTask.raci?.responsible || [],
        accountable: parentTask.raci?.accountable || '',
        consulted: parentTask.raci?.consulted || [],
        informed: parentTask.raci?.informed || [],
      },
    },
  });

  // Reset form when modal opens with parent task data
  useEffect(() => {
    if (open) {
      reset({
        title: '',
        description: '',
        priority: parentTask.priority,
        startDate: parentTask.startDate ? dayjs(parentTask.startDate) : dayjs(),
        dueDate: parentTask.dueDate ? dayjs(parentTask.dueDate) : dayjs().add(1, 'day'),
        estimatedMinutes: 60,
        isOnsite: parentTask.isOnsite || false,
        onsiteLocation: parentTask.onsiteLocation || '',
        raci: {
          responsible: parentTask.raci?.responsible || [],
          accountable: parentTask.raci?.accountable || '',
          consulted: parentTask.raci?.consulted || [],
          informed: parentTask.raci?.informed || [],
        },
      });
    }
  }, [open, parentTask, reset]);

  // Re-validate estimated time when budget changes
  useEffect(() => {
    if (open && effectiveMaxMinutes >= 0) {
      trigger('estimatedMinutes');
    }
  }, [effectiveMaxMinutes, open, trigger]);

  // Create subtask mutation
  const createMutation = useMutation({
    mutationFn: (data: Partial<Task>) =>
      taskService.createSubtask(parentTask.id, data),
    onSuccess: (response) => {
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });
      if (parentTask.projectId) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.projectTasks(parentTask.projectId),
        });
      }
      showSuccess('Subtask created successfully!');
      handleClose();
      onSuccess?.(response);
    },
    onError: (error: any) => {
      console.error('[CreateSubtask] Error creating subtask:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create subtask';
      showError(errorMessage);
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data: SubtaskFormData) => {
    const payload: Partial<Task> = {
      title: data.title,
      description: data.description || '',
      priority: data.priority,
      status: TaskStatus.NOT_STARTED,
      startDate: data.startDate ? dayjs(data.startDate).format('YYYY-MM-DD') : undefined,
      dueDate: dayjs(data.dueDate).format('YYYY-MM-DD'),
      estimatedMinutes: data.estimatedMinutes || undefined,
      projectId: parentTask.projectId,
      milestoneId: parentTask.milestoneId,
      parentTaskId: parentTask.id,
      isOnsite: data.isOnsite || false,
      onsiteLocation: data.onsiteLocation,
      raci: {
        responsible: data.raci.responsible || [],
        accountable: data.raci.accountable || '',
        consulted: data.raci.consulted || [],
        informed: data.raci.informed || [],
      },
      tags: parentTask.tags || [],
      blockedByTaskIds: [],
      blockingTaskIds: [],
      subtaskIds: [],
      order: (parentTask.subtaskIds?.length || 0) + 1,
      attachments: [],
      requiresReview: false,
    };
    createMutation.mutate(payload);
  };

  const getStatusColor = (status: TaskStatus): string => {
    switch (status) {
      case TaskStatus.COMPLETED:
        return '#16a34a';
      case TaskStatus.IN_PROGRESS:
        return '#2563eb';
      case TaskStatus.BLOCKED:
        return '#f59e0b';
      default:
        return '#64748b';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GitBranch size={20} style={{ color: '#64748b' }} />
              <Typography variant="h6" fontWeight={600}>
                Add Subtask
              </Typography>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        {/* Parent Task Info */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            bgcolor: 'grey.50',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            Parent Task
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Chip
              size="small"
              label={parentTask.status.replace('_', ' ')}
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: `${getStatusColor(parentTask.status)}15`,
                color: getStatusColor(parentTask.status),
                fontWeight: 500,
              }}
            />
            <Typography variant="body2" fontWeight={500} noWrap>
              {parentTask.title}
            </Typography>
            <ArrowRight size={14} style={{ color: '#9ca3af' }} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: 'italic' }}
            >
              New subtask
            </Typography>
          </Box>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Inheritance Info */}
              <Alert severity="info" sx={{ py: 0.5 }}>
                <Typography variant="caption">
                  This subtask will inherit project, milestone, tags, and RACI from parent. You can modify any field.
                </Typography>
              </Alert>

              {/* Time Budget Indicator */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: timeBudget.remaining < 0 ? 'error.main' : timeBudget.usagePercent > 90 ? 'warning.main' : 'divider',
                  bgcolor: timeBudget.remaining < 0 ? 'error.lighter' : timeBudget.usagePercent > 90 ? 'warning.lighter' : 'grey.50',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Time Budget Allocation
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={timeBudget.remaining < 0 ? 'error.main' : timeBudget.usagePercent > 90 ? 'warning.main' : 'success.main'}
                  >
                    {formatPercent(timeBudget.usagePercent)} used
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(timeBudget.usagePercent, 100)}
                  color={timeBudget.remaining < 0 ? 'error' : timeBudget.usagePercent > 90 ? 'warning' : 'success'}
                  sx={{ height: 8, borderRadius: 1, mb: 1.5 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Parent Task Time:
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {timeBudget.parentTotal} min
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Existing Subtasks ({timeBudget.subtasksCount}):
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {timeBudget.existingSubtasksTotal} min
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Remaining:
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={timeBudget.remaining < 0 ? 'error.main' : 'success.main'}
                    >
                      {timeBudget.remaining} min
                    </Typography>
                  </Box>
                </Box>
                {timeBudget.remaining < 0 && (
                  <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>
                    <Typography variant="caption">
                      ⚠️ Subtasks already exceed parent task time by <strong>{Math.abs(timeBudget.remaining)} minutes</strong>.
                      You cannot add more subtasks unless you increase the parent task's estimated time.
                    </Typography>
                  </Alert>
                )}
                {timeBudget.remaining >= 0 && timeBudget.remaining < 60 && (
                  <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
                    <Typography variant="caption">
                      ⚠️ Less than {timeBudget.remaining} minutes remaining. Consider allocating less time or increasing parent task time.
                    </Typography>
                  </Alert>
                )}
              </Paper>

              {/* Title */}
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Subtask Title"
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    placeholder="Enter subtask title..."
                    autoFocus
                  />
                )}
              />

              {/* Description */}
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description (optional)"
                    fullWidth
                    multiline
                    rows={2}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    placeholder="Brief description..."
                  />
                )}
              />

              {/* Priority & Assignee Row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {/* Priority */}
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <AppSelect label="Priority" {...field}>
                        <AppMenuItem value={TaskPriority.P4}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#94a3b8',
                              }}
                            />
                            P4 - Low
                          </Box>
                        </AppMenuItem>
                        <AppMenuItem value={TaskPriority.P3}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#2563eb',
                              }}
                            />
                            P3 - Medium
                          </Box>
                        </AppMenuItem>
                        <AppMenuItem value={TaskPriority.P2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#f59e0b',
                              }}
                            />
                            P2 - High
                          </Box>
                        </AppMenuItem>
                        <AppMenuItem value={TaskPriority.P1}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#ef4444',
                              }}
                            />
                            P1 - Critical
                          </Box>
                        </AppMenuItem>
                    </AppSelect>
                  )}
                />
              </Box>

              {/* Start Date & Due Date Row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {/* Start Date */}
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Start Date"
                      value={field.value}
                      onChange={(newValue) => field.onChange(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.startDate,
                          helperText: errors.startDate?.message as string,
                        },
                      }}
                    />
                  )}
                />

                {/* Due Date */}
                <Controller
                  name="dueDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Due Date"
                      value={field.value}
                      onChange={(newValue) => field.onChange(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.dueDate,
                          helperText: errors.dueDate?.message as string,
                        },
                      }}
                    />
                  )}
                />
              </Box>

              {/* Estimated Time Row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                {/* Estimated Time */}
                <Controller
                  name="estimatedMinutes"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (value === undefined || value === null) return true;
                      if (value < 0) return 'Must be positive';
                      // Check against remaining time budget first (more restrictive)
                      if (timeBudget.remaining <= 0) {
                        return 'No remaining time in parent task budget';
                      }
                      if (value > effectiveMaxMinutes) {
                        const hours = Math.floor(effectiveMaxMinutes / 60);
                        const mins = effectiveMaxMinutes % 60;
                        return `Maximum ${hours}h ${mins}m remaining in budget`;
                      }
                      return true;
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Estimate (minutes)"
                      type="number"
                      fullWidth
                      error={!!errors.estimatedMinutes}
                      helperText={
                        errors.estimatedMinutes?.message || 
                        (timeBudget.remaining > 0 
                          ? `Remaining: ${Math.floor(timeBudget.remaining / 60)}h ${Math.round(timeBudget.remaining % 60)}m (${Math.round(timeBudget.remaining)} min)`
                          : `⚠️ No time remaining in parent task budget`)
                      }
                      InputProps={{
                        inputProps: { 
                          min: 0, 
                          max: effectiveMaxMinutes,
                          step: 1
                        },
                      }}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                      disabled={timeBudget.remaining <= 0}
                    />
                  )}
                />
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* Onsite Section */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Location
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Controller
                    name="isOnsite"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Is Onsite"
                      />
                    )}
                  />

                  {watch('isOnsite') && (
                    <Controller
                      name="onsiteLocation"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Onsite Location"
                          fullWidth
                          size="small"
                          error={!!errors.onsiteLocation}
                          helperText={errors.onsiteLocation?.message}
                          placeholder="Enter location (e.g., Client Office)"
                        />
                      )}
                    />
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* RACI Section */}
              <Typography variant="subtitle2" fontWeight={600}>
                RACI Assignment (Inherited from parent - changeable)
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {/* Responsible */}
                <Controller
                  name="raci.responsible"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      options={users}
                      getOptionLabel={(option) => userDisplayName(option)}
                      value={users.filter((u) => (field.value || []).includes(u.id))}
                      onChange={(_, newValue) =>
                        field.onChange(newValue.map((u) => u.id))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Responsible (R) *"
                          error={!!errors.raci?.responsible}
                          helperText={
                            errors.raci?.responsible?.message ||
                            'Who does the work'
                          }
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <Chip
                                  label="R"
                                  size="small"
                                  sx={{
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    fontWeight: 600,
                                    mr: 1,
                                  }}
                                />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            avatar={
                              <Avatar sx={{ bgcolor: 'primary.main' }}>
                                {userDisplayName(option).charAt(0)}
                              </Avatar>
                            }
                            label={userDisplayName(option)}
                            size="small"
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                    />
                  )}
                />

                {/* Accountable */}
                <Controller
                  name="raci.accountable"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={users}
                      getOptionLabel={(option) => userDisplayName(option)}
                      value={users.find((u) => u.id === field.value) || null}
                      onChange={(_, newValue) =>
                        field.onChange(newValue ? newValue.id : '')
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Accountable (A) *"
                          error={!!errors.raci?.accountable}
                          helperText={
                            errors.raci?.accountable?.message ||
                            'Single person who is answerable'
                          }
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <Chip
                                  label="A"
                                  size="small"
                                  sx={{
                                    bgcolor: 'warning.main',
                                    color: 'white',
                                    fontWeight: 600,
                                    mr: 1,
                                  }}
                                />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  )}
                />

                {/* Consulted */}
                <Controller
                  name="raci.consulted"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      options={users}
                      getOptionLabel={(option) => userDisplayName(option)}
                      value={users.filter((u) => (field.value || []).includes(u.id))}
                      onChange={(_, newValue) =>
                        field.onChange(newValue.map((u) => u.id))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Consulted (C) *"
                          error={!!errors.raci?.consulted}
                          helperText={
                            errors.raci?.consulted?.message ||
                            'Who provides input'
                          }
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <Chip
                                  label="C"
                                  size="small"
                                  sx={{
                                    bgcolor: 'info.main',
                                    color: 'white',
                                    fontWeight: 600,
                                    mr: 1,
                                  }}
                                />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            avatar={
                              <Avatar sx={{ bgcolor: 'info.main' }}>
                                {userDisplayName(option).charAt(0)}
                              </Avatar>
                            }
                            label={userDisplayName(option)}
                            size="small"
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                    />
                  )}
                />

                {/* Informed */}
                <Controller
                  name="raci.informed"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      options={users}
                      getOptionLabel={(option) => userDisplayName(option)}
                      value={users.filter((u) => (field.value || []).includes(u.id))}
                      onChange={(_, newValue) =>
                        field.onChange(newValue.map((u) => u.id))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Informed (I) *"
                          error={!!errors.raci?.informed}
                          helperText={
                            errors.raci?.informed?.message ||
                            'Who needs updates'
                          }
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <Chip
                                  label="I"
                                  size="small"
                                  sx={{
                                    bgcolor: 'secondary.main',
                                    color: 'white',
                                    fontWeight: 600,
                                    mr: 1,
                                  }}
                                />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            avatar={
                              <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                {userDisplayName(option).charAt(0)}
                              </Avatar>
                            }
                            label={userDisplayName(option)}
                            size="small"
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                    />
                  )}
                />
              </Box>
            </Box>
          </DialogContent>

          <Divider />

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose} variant="outlined" color="inherit">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || createMutation.isPending || !isValid || timeBudget.remaining <= 0}
              startIcon={<GitBranch size={16} />}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Subtask'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CreateSubtaskModal;
